import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import FolderOpenOutlinedIcon from '@mui/icons-material/FolderOpenOutlined';
import { Box, ButtonBase, Skeleton, Stack, Typography } from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { useCommonApi } from '../../api';
import { PATHS } from '../../paths';
import { DASHBOARD_RADIUS, dashboardCardSx } from '../common';

interface RequestsWidgetProps {
  pendingCount?: number;
  onShowAll?: () => void;
  isLoading?: boolean;
}

const AccidentsWidget = ({ pendingCount = 0, onShowAll, isLoading }: RequestsWidgetProps) => (
  <ButtonBase
    onClick={onShowAll}
    focusRipple={false}
    sx={{
      ...dashboardCardSx,
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'stretch',
      justifyContent: 'space-between',
      gap: 3,
      textAlign: 'left',
      width: 1,
      height: 1,
      minHeight: { xs: 128, md: 208 },
      padding: { xs: 2, md: 3 },
      borderRadius: DASHBOARD_RADIUS,
      '&:focus-visible': { outline: '3px solid', outlineColor: 'primary.main', outlineOffset: 3 },
      '@media (prefers-reduced-motion: no-preference)': {
        transition: 'transform 0.35s cubic-bezier(0.16, 1, 0.3, 1), border-color 0.2s',
        '& .accidents-arrow': { transition: 'transform 0.35s cubic-bezier(0.16, 1, 0.3, 1)' },
        '&:hover': { transform: 'translateY(-2px)' },
        '&:hover .accidents-arrow': { transform: 'translateX(3px)' },
        '&:active': { transform: 'translateY(0) scale(0.99)' }
      },
      '&:hover': { borderColor: 'primary.main' }
    }}
  >
    <Stack direction="row" alignItems="center" gap={1.5}>
      <Box
        sx={{
          display: 'grid',
          placeItems: 'center',
          width: 40,
          height: 40,
          borderRadius: 2,
          color: 'primary.main',
          backgroundColor: 'rgba(198, 35, 39, 0.08)'
        }}
      >
        <FolderOpenOutlinedIcon />
      </Box>
      <Typography variant="body2" fontWeight={600} sx={{ lineHeight: 1.3 }}>
        Moje otevřené nehody
      </Typography>
    </Stack>
    <Stack direction="row" justifyContent="space-between" alignItems="flex-end">
      {isLoading ? (
        <Skeleton variant="rounded" width={72} height={64} />
      ) : (
        <Typography
          color={pendingCount > 0 ? 'primary' : 'text.primary'}
          sx={{
            fontSize: '4rem',
            fontWeight: 700,
            lineHeight: 0.9,
            letterSpacing: '-0.03em',
            fontVariantNumeric: 'tabular-nums'
          }}
        >
          {pendingCount}
        </Typography>
      )}
      <Stack direction="row" alignItems="center" gap={0.5} color="primary.main" fontWeight={600}>
        <Typography component="span" fontWeight={600} fontSize="0.9rem">
          Zobrazit
        </Typography>
        <ArrowForwardIcon className="accidents-arrow" fontSize="small" />
      </Stack>
    </Stack>
  </ButtonBase>
);

export const AccidentsWidgetConnected = () => {
  const navigate = useNavigate();
  const { data: dashboardStatisticsData, isLoading } = useQuery(useCommonApi().getDashboardStatistics());

  return (
    <AccidentsWidget
      isLoading={isLoading}
      pendingCount={dashboardStatisticsData?.countAccidentsOpened}
      onShowAll={() => navigate(PATHS.ACCIDENTS())}
    />
  );
};
